const Signup = require('./containers/Signup');
const SpecificSchoolSignup = require('./containers/SpecificSchoolSignup');
const SpecificParentSignup = require('./containers/SpecificParentSignup');
const VerifyEmail = require('./containers/VerifyEmail');
const ChoosePassword = require('./containers/ChoosePassword');
const AgreeToTerms = require('./components/AgreeToTerms');
const UserInfo = require('./containers/UserInfo');
const { context } = require('app-context');
const PlainLayout = require('layouts/PlainLayout');
const { customTeamSignups, customSignups, resolveCustomSignupSlug } = require('utils/customSignups');
const LoadingScreen = require('components/LoadingScreen');
const AuthConstants = require('services/auth-management/constants');
const AuthPersistence = require('services/auth-management/persistence');
const AuthStateTransitions = require('services/auth-management/state-transitions');
const SignupState = require('services/auth-management/signup-state');
const RoutingTable = require('services/auth-management/routing-table');
const {
    getSignupContextWithQueryParams,
    hasSignupQueryParams,
    hasWelcomeEmailContext
} = require('utils/signup-context');
const GetQueryParams = require('utils/get-query-params');
const { isCordovaRunning } = require('utils/cordova');

const { initializeAppForSignup } = require('utils/initializeAppForSignup');

const redux = context.redux.hooks;
const selectors = context.selectors.all;
const actions   = context.actions;
const handlers = context.handlers;

const isTeamsApp = process.env.APP_FLAVOUR === 'teams';

const internals = {};
internals.signupEntryVersion = 0;
internals.getInviteLifecycleKey = ({ location }) => hasWelcomeEmailContext(location)
    ? location.key || location.search
    : undefined;

internals.ensureSchoolsLoaded = async () => {

    if (selectors.getSchools(redux.getState()).length) {
        return;
    }

    await redux.dispatch(actions.dataFetching.fetchSchools());
};

internals.customSchoolSignupPrecheck = async ({ match: { params } }, { prospect } = {}) => {

    if (prospect && isTeamsApp) {
        return { replace: '/signup' };
    }

    const supported = (isTeamsApp && !prospect) ? customTeamSignups : customSignups;
    const slug = resolveCustomSignupSlug(params.slug);

    if (!supported[slug]) {
        if (isCordovaRunning() && slug.includes('android_asset')) {
            return { replace: RoutingTable.getAppLoaderRoute() };
        }

        return { replace: '/signup' };
    }

    await internals.ensureSchoolsLoaded();
};

exports = module.exports = () => [{
    path: AuthConstants.authFlow.routes.signup.entry,
    component: PlainLayout,
    fallback: LoadingScreen,
    onWillMount: internals.signupPrecheck,
    getLifecycleKey: internals.getInviteLifecycleKey,
    childRoutes: [
        {
            path: '/verify-email',
            fallback: LoadingScreen,
            component: VerifyEmail
        },
        {
            path: '/choose-password',
            fallback: LoadingScreen,
            component: ChoosePassword,
            onWillMount: internals.choosePasswordPrecheck
        },
        {
            path: '/user-info',
            fallback: LoadingScreen,
            component: UserInfo,
            onWillMount: internals.userInfoPrecheck
        },
        {
            path: '/confirm-password',
            fallback: LoadingScreen,
            component: ChoosePassword
        }
    ]
},
{
    path: AuthConstants.authFlow.routes.signup.terms,
    exact: true,
    component: AgreeToTerms,
    onWillMount: () => {

        const signupContext = selectors.getSignupContext(redux.getState());
        const { verificationToken } = signupContext;

        if (!verificationToken) {
            return { replace: AuthConstants.authFlow.routes.signup.entry };
        }

        if (AuthPersistence.getBrowserTermsAgreement()) {
            return {
                replace: RoutingTable.getIncompleteSignupRoute({
                    signupContext: {
                        ...signupContext,
                        agreeToTerms: true
                    }
                })
            };
        }
    }
}];

exports.signupRoute = {
    path: AuthConstants.authFlow.routes.signup.entry,
    exact: true,
    component: Signup,
    onWillMount: (...args) => internals.signupLandingPrecheck(...args)
};

exports.specificParent = {
    path: '/:slug/family',
    component: SpecificParentSignup,
    onWillMount: async ({ match: { params } }) => {

        const supported = {
            xula: true
        };

        const slug = String(params.slug).toLowerCase();

        if (!supported[slug]) {
            return { replace: '/signup' };
        }

        try {
            await internals.ensureSchoolsLoaded();
            await handlers.signup.resolveSchoolExists(slug);
        }
        catch (err) {

            return { replace: '/signup' };
        }
    }
};

exports.specificProspect = {
    path: '/:slug/prospect',
    component: SpecificSchoolSignup,
    onWillMount: (route) => internals.customSchoolSignupPrecheck(route, { prospect: true })
};

exports.specificSchool = {
    path: '/:slug',
    component: SpecificSchoolSignup,
    onWillMount: (route) => internals.customSchoolSignupPrecheck(route)
};

// This gets used in signupPrecheck above, and for sso in src/routes/sso
exports.initSignupContext = async (location) => {

    const version = ++internals.signupEntryVersion;
    const isCurrent = () => version === internals.signupEntryVersion;

    await handlers.auth.resolveIsAuthenticated();

    if (!isCurrent()) {
        return null;
    }

    const { v: routeToken } = GetQueryParams(location);
    const previousSignupContext = selectors.getSignupContext(redux.getState());
    const shouldResetSignupContext = hasWelcomeEmailContext(location)
        || (typeof routeToken === 'string' && !!routeToken &&
            (previousSignupContext.verificationToken !== routeToken || !internals.hasRequiredSignupContext(previousSignupContext)));

    if (shouldResetSignupContext && selectors.getIsAuthenticated(redux.getState())) {
        await redux.dispatch(actions.auth.logout({ redirect: false }));

        if (!isCurrent()) {
            return null;
        }

        if (selectors.getIsAuthenticated(redux.getState())) {
            await redux.dispatch(actions.auth.logoutSync({ redirect: false }));
        }

        if (selectors.getIsAuthenticated(redux.getState())) {
            throw new Error('Cannot continue welcome-link signup with an authenticated session');
        }
    }

    if (shouldResetSignupContext) {
        AuthStateTransitions.resetForWelcomeLink();
    }

    let signupContext;
    let inviteContext;

    try {
        if (shouldResetSignupContext) {
            const { v: verificationToken } = GetQueryParams(location);
            redux.dispatch(actions.signup.initContext({ verificationToken }));
            const result = await redux.dispatch(actions.signup.authenticate.checkPassword({ verificationToken }));

            if (!isCurrent()) {
                return null;
            }

            if (result?.error) {
                throw result.payload?.error || new Error('Unable to resolve invitation');
            }

            inviteContext = { ...internals.getPasswordCheckData(result), verificationToken };

            if (!internals.hasRequiredSignupContext(inviteContext)) {
                redux.dispatch(actions.alerts.notification.push({ message: 'This invitation link has expired.' }));
                return null;
            }

            redux.dispatch(actions.signup.updateContext(inviteContext));
        }

        signupContext = await getSignupContextWithQueryParams(context, location, { inviteContext });
    }
    catch (err) {
        if (!isCurrent()) {
            return null;
        }

        if ([404, 410].includes(err?.response?.status)) {
            redux.dispatch(actions.alerts.notification.push({ message: 'This invitation link has expired.' }));
            return null;
        }

        if (err?.code === 'SIGNUP_CONTEXT_SCHOOL_LOOKUP_FAILED') {
            return null;
        }

        throw err;
    }

    if (!isCurrent()) {
        return null;
    }

    redux.dispatch(actions.signup.updateContext(signupContext));

    const { verificationToken } = signupContext;

    // Remainder of this path only matters if we have a verification token
    // This also gets called on /signup/sso.
    if (!verificationToken) {
        return selectors.getSignupContext(redux.getState());
    }

    // Ensure user signup progress info is fetched
    if (!signupContext.fetchedProgress) {
        // Fetch user info and save into redux
        await redux.dispatch(actions.signup.profile.fetchSignupProgress({
            verificationToken
        }));
    }

    if (!isCurrent()) {
        return null;
    }

    if (!inviteContext) {
        await redux.dispatch(actions.signup.authenticate.checkPassword({ verificationToken }));
    }

    if (!isCurrent()) {
        return null;
    }

    const checkedSignupContext = selectors.getSignupContext(redux.getState());

    if (checkedSignupContext.authMethod !== AuthConstants.authFlow.methods.sso
        && checkedSignupContext.isEmailVerified === false) {
        await redux.dispatch(actions.signup.verifyEmail({
            verificationToken
        }));
    }

    return isCurrent() ? selectors.getSignupContext(redux.getState()) : null;
};

internals.getPasswordCheckData = (result = {}) => result?.payload?.result?.data
    || result?.payload?.result
    || result?.result?.data
    || result?.result
    || result?.data
    || result
    || {};

internals.mergeSignupRouteProgress = ({ currentContext, progress }) => SignupState.mergeSignupProgressContext({
    currentContext,
    progress,
    source: AuthConstants.authFlow.sources.signupRouteProgress
});

internals.getSignupContextFromQuery = (base = {}, location) => {

    const { verificationToken } = RoutingTable.getAuthRouteContext(location);

    if (!verificationToken) {
        return base;
    }

    return base.verificationToken === verificationToken
        ? base
        : { ...AuthConstants.signupContext.resetFields, verificationToken };
};

internals.isUserInfoRoute = (route = '') => route.startsWith(AuthConstants.authFlow.routes.signup.profile);

internals.hasRequiredSignupContext = ({
    verificationToken,
    email,
    schoolId,
    roleId
} = {}) => typeof verificationToken === 'string'
    && !!verificationToken.trim()
    && typeof email === 'string'
    && !!email.trim()
    && Number.isFinite(Number(schoolId))
    && Number(schoolId) > 0
    && Number.isFinite(Number(roleId))
    && Number(roleId) > 0;

internals.getSignupRouteDebugDetails = ({ location, signupContext, state } = {}) => ({
    location,
    query: GetQueryParams(location),
    reduxSignupContext: selectors.getSignupContext(state || redux.getState()),
    signupContext,
    windowLocation: typeof window === 'undefined' ? null : {
        href: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search
    }
});

internals.signupLandingPrecheck = async (route) => {

    const location = route?.location;
    const {
        v,
        email,
        s,
        r
    } = GetQueryParams(location);

    if (!v) {
        if (hasSignupQueryParams(location)) {
            await exports.initSignupContext(location);
        }

        return;
    }

    const initialSignupContext = {
        verificationToken: v,
        email,
        schoolId: s ? Number(s) : undefined,
        roleId: r ? Number(r) : undefined
    };

    redux.dispatch(actions.signup.initContext(initialSignupContext));

    let passwordCheck = {};

    try {
        const result = await redux.dispatch(actions.signup.authenticate.checkPassword({
            verificationToken: v
        }));

        passwordCheck = internals.getPasswordCheckData(result);
    }
    catch (err) {
        return { replace: RoutingTable.getIncompleteSignupRoute({
            signupContext: initialSignupContext
        }) };
    }

    const signupContext = internals.mergeSignupRouteProgress({
        currentContext: {
            ...initialSignupContext,
            ...selectors.getSignupContext(redux.getState())
        },
        progress: passwordCheck
    });

    return { replace: RoutingTable.getIncompleteSignupRoute({ signupContext }) };
};

internals.signupPrecheck = async ({ location }) => {

    const { pathname } = location;

    // Skip logic for verify-email route, which just relays info that an email was sent
    if (pathname.includes('verify-email')) {
        return;
    }

    // Saves signupContext from query params
    // Waits for auth to resolve
    const signupContext = await exports.initSignupContext(location);

    if (!signupContext) {
        return { replace: AuthConstants.authFlow.routes.login.entry };
    }
    const schoolId = Number(signupContext.schoolId);
    const choosePasswordPageLocation = internals.getChoosePasswordRoute();

    // Wait for auth to settle so we know if the user has a Nearpeer token or not

    let state = redux.getState();

    const userIsAuthenticated = selectors.getIsAuthenticated(state);

    const switchableCommunityIds = signupContext.joinedSchoolIds || [];
    const hasJoinedCommunity = switchableCommunityIds.includes(schoolId);
    const signupDestination = RoutingTable.getIncompleteSignupRoute({
        signupContext,
        hasJoinedCommunity,
        isAuthenticated: userIsAuthenticated
    });

    // If user is not logged-in.
    if (!userIsAuthenticated) {
        if (signupDestination === AuthConstants.authFlow.routes.app.loader) {
            // Redirect to login and show toast

            redux.dispatch(actions.alerts.notification.push({
                message: 'Please login, you already have an account.'
            }));

            return { replace: RoutingTable.getAppLoaderRoute() };
        }

        if (signupDestination !== pathname) {
            return { replace: signupDestination };
        }

        return;
    }

    // User is logged-in with Nearpeer token. selectors.getIsAuthenticated() checks for the Nearpeer token.
    let currentUser = selectors.getCurrentUser(state).user();

    if (!currentUser) {
        try {
            await redux.dispatch(actions.dataFetching.fetchCurrentUser());
        }
        catch (err) {
            // Let the child signup prechecks handle missing context/auth instead of immediately logging out.
        }

        state = redux.getState();
        currentUser = selectors.getCurrentUser(state).user();
    }

    if (!currentUser) {
        return signupContext;
    }

    // This catches the case where a user clicks on an invite link from an email for a different account.
    if (currentUser.email !== signupContext.email) {
        // Logout current user
        await redux.dispatch(actions.auth.logout({ redirect: choosePasswordPageLocation }));
        return;
    }

    if (currentUser.verified === false) {
        return signupContext;
    }

    // This is the same user as in the email, now to find out if it's for the current community or a different one.
    // If it's a different one we need to know if the user has joined or not, to know how to redirect.

    const isCurrentCommunity = currentUser.schoolId === schoolId;

    if (isCurrentCommunity) {
        return { replace: AuthConstants.authFlow.routes.app.welcome };
    }

    let redirect = RoutingTable.REDIRECTS.welcome;

    if (!hasJoinedCommunity) {
        // Has not joined community, join in the background and redirect to profile confirm page
        await redux.dispatch(actions.profileManagement.joinSchool({
            schoolId
        }));

        redirect = RoutingTable.REDIRECTS.joinSchoolProfile;
    }

    // Switch to welcome email community
    await redux.dispatch(actions.profileManagement.switchSchool({
        schoolId,
        redirect
    }));

    return signupContext;
};

internals.getChoosePasswordRoute = function getChoosePasswordRoute() {

    return AuthConstants.authFlow.routes.signup.password;
};

internals.getSignupUserInfoRoute = function getSignupUserInfoRoute() {

    return AuthConstants.authFlow.routes.signup.profile;
};

internals.getSignupTermsRoute = function getSignupTermsRoute(signupContext = {}) {

    const routes = AuthConstants.authFlow.routes.signup;

    return signupContext.authMethod === AuthConstants.authFlow.methods.sso ? routes.ssoTerms : routes.terms;
};

internals.getSignupSsoRoute = function getSignupSsoRoute() {

    return AuthConstants.authFlow.routes.signup.sso;
};

// Users land here from their verification emails
internals.choosePasswordPrecheck = async ({ location } = {}) => {

    let version = internals.signupEntryVersion;
    let state = redux.getState();
    let signupContext = internals.getSignupContextFromQuery(selectors.getSignupContext(state), location);

    if (signupContext.verificationToken && !internals.hasRequiredSignupContext(signupContext)) {
        const resolvedContext = await exports.initSignupContext(location);

        if (!resolvedContext) {
            return { replace: AuthConstants.authFlow.routes.home };
        }

        signupContext = resolvedContext;
        version = internals.signupEntryVersion;
    }

    const {
        verificationToken
    } = signupContext;

    if (!internals.hasRequiredSignupContext(signupContext)) {
        redux.dispatch(actions.alerts.notification.push({
            message: 'This invitation link has expired.'
        }));

        return { replace: AuthConstants.authFlow.routes.home };
    }

    redux.dispatch(actions.signup.updateContext(signupContext));

    let passwordCheck = {};

    try {
        const result = await redux.dispatch(actions.signup.authenticate.checkPassword({
            verificationToken
        }));

        passwordCheck = internals.getPasswordCheckData(result);
    }
    catch (err) {
        console.warn('[signup-route] checkPassword failed in choosePasswordPrecheck', {
            error: err,
            location,
            signupContext
        });
        return;
    }

    if (version !== internals.signupEntryVersion) {
        return;
    }

    state = redux.getState();
    signupContext = internals.mergeSignupRouteProgress({
        currentContext: selectors.getSignupContext(state),
        progress: passwordCheck
    });

    if (signupContext.authMethod !== AuthConstants.authFlow.methods.sso && signupContext.isEmailVerified === false) {
        await redux.dispatch(actions.signup.verifyEmail({
            verificationToken
        }));
    }

    const nextRoute = RoutingTable.getIncompleteSignupRoute({ signupContext });

    if (version !== internals.signupEntryVersion) {
        return;
    }

    if (nextRoute === internals.getChoosePasswordRoute()) {
        return;
    }

    return { replace: nextRoute };
};

internals.userInfoPrecheck = async ({ location } = {}) => {

    let state = redux.getState();

    const signupContextFromQuery = internals.getSignupContextFromQuery(selectors.getSignupContext(state), location);
    const {
        verificationToken,
        schoolId
    } = signupContextFromQuery;

    if (!verificationToken || !schoolId) {
        console.warn(
            '[signup-route] userInfoPrecheck missing verificationToken or schoolId; restarting signup',
            internals.getSignupRouteDebugDetails({
                location,
                signupContext: signupContextFromQuery,
                state
            })
        );

        return { replace: AuthConstants.authFlow.routes.signup.entry };
    }

    let passwordCheck = {};

    try {
        const result = await redux.dispatch(actions.signup.authenticate.checkPassword({
            verificationToken
        }));

        passwordCheck = internals.getPasswordCheckData(result);
    }
    catch (err) {
        console.warn('[signup-route] checkPassword failed in userInfoPrecheck', {
            error: err,
            location,
            signupContext: signupContextFromQuery
        });
        return { replace: internals.getChoosePasswordRoute() };
    }

    state = redux.getState();

    let signupContext = internals.mergeSignupRouteProgress({
        currentContext: selectors.getSignupContext(state),
        progress: passwordCheck
    });

    let nextRoute = RoutingTable.getIncompleteSignupRoute({ signupContext });

    if (!internals.isUserInfoRoute(nextRoute)) {
        return { replace: nextRoute };
    }

    const school = selectors.getSchoolById(redux.getState(), schoolId);

    if (!school && verificationToken) {
        await redux.dispatch(actions.dataFetching.fetchSchoolByVerificationToken({
            token: verificationToken
        }));
    }

    signupContext = internals.mergeSignupRouteProgress({
        currentContext: selectors.getSignupContext(redux.getState()),
        progress: passwordCheck
    });

    if (!signupContext.fetchedProgress && verificationToken) {
        await redux.dispatch(actions.signup.profile.fetchSignupProgress({
            verificationToken
        }));
    }

    await initializeAppForSignup(context);

    state = redux.getState();
    signupContext = internals.mergeSignupRouteProgress({
        currentContext: selectors.getSignupContext(state),
        progress: passwordCheck
    });
    nextRoute = RoutingTable.getIncompleteSignupRoute({ signupContext });

    if (!internals.isUserInfoRoute(nextRoute)) {
        return { replace: nextRoute };
    }
};
